import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Observable,
  catchError,
  of,
  forkJoin,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import {
  UserService,
  ContextService,
  ConfigurationService,
  FeatureService,
  LocaleService,
  GlobalStore,
} from '@maximizer/core/shared/data-access';
import {
  Session,
  Octopus,
  ConfigurationSettingId,
  ConfigurationSetting,
  User,
  SystemConfiguration,
} from '@maximizer/core/shared/domain';
import { InsightsService } from '@maximizer/core/shared/insights';
import { OAuth2Service } from '@maximizer/oauth2/data-access';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { OutlookInsightsEvents } from '../models';
import { getDecodedAccessToken } from '../utils';
import { normalizeError } from '@maximizer/shared/util';
import { OutlookStore } from '../state';
import { OutlookService } from './outlook/outlook.service';

const LOGIN_PATH = '/login';

type AssembledSessionData = {
  session: Session;
  user: User | null;
  settings: ConfigurationSetting[] | null;
  configuration: SystemConfiguration;
};

type CachedSessionData = {
  session: Octopus.SessionResponse;
  timestamp: number;
};

@Injectable()
export class OutlookSessionService {
  private readonly insightsService = inject(InsightsService);
  private readonly http = inject(HttpClient);
  private readonly users = inject(UserService);
  private readonly context = inject(ContextService);
  private readonly configuration = inject(ConfigurationService);
  private readonly feature = inject(FeatureService);
  private readonly locale = inject(LocaleService);
  private readonly global = inject(GlobalStore);
  private readonly outlookStore = inject(OutlookStore);
  private readonly outlookService = inject(OutlookService);
  private readonly oauth2Service = inject(OAuth2Service);

  private get endpoint(): string {
    return `${this.context.api}${Octopus.Action.SESSION_INFO}`;
  }

  private get sessionCacheKey(): string {
    const partitionKey = this.outlookService.storagePartitionKey.toLowerCase();
    return `${partitionKey}:Mx-Session`;
  }

  private readonly SESSION_CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
  private sessionRequest?: Observable<Session | undefined>;
  private lastSessionErrorType?: string;

  isLastErrorLocalNetwork(): boolean {
    return this.lastSessionErrorType === 'session-network-error';
  }

  getInfo(): Observable<Session | undefined> {
    if (this.sessionRequest) {
      return this.sessionRequest;
    }

    const cachedSession = this.retrieveSessionResponseFromStorage(
      this.sessionCacheKey,
    );

    this.sessionRequest = (
      cachedSession
        ? of(cachedSession)
        : this.requestSessionResponse().pipe(
            tap((response) => this.storeSessionResponseInStorage(response)),
          )
    ).pipe(
      switchMap((response) => this.assembleSessionData(response)),
      map((assembled) => this.enrichSessionData(assembled)),
      switchMap((session) => this.initializeLocaleAndFeatures(session)),
      tap((session) => this.finalizeSessionSetup(session)),
      shareReplay(1),
      catchError((error) => {
        this.sessionRequest = undefined;
        return this.handleSessionError(error);
      }),
    );

    return this.sessionRequest;
  }

  private buildRequestBody(): Octopus.SessionReadRequest {
    return {
      AddressBook: {
        Alias: 1,
        Database: 1,
        DisplayValue: 1,
        WorkSpaceId: 1,
        UDBID: 1,
        ...(this.context.isCloud && { CustomerId: 1 }),
      },
      User: {
        Key: {
          Value: 1,
          Uid: 1,
        },
      },
    };
  }

  private requestSessionResponse(): Observable<Octopus.SessionResponse> {
    return this.http.post<Octopus.SessionResponse>(
      this.endpoint,
      this.buildRequestBody(),
    );
  }

  private assembleSessionData(
    result: Octopus.SessionResponse,
  ): Observable<AssembledSessionData> {
    if (!result?.Data) {
      throw new Error('Invalid session response: Missing Data object');
    }

    if (!result.Data.AddressBook) {
      throw new Error('Invalid session response: Missing AddressBook data');
    }

    const userId = result.Data.User?.Key?.Value;
    const userUid = result.Data.User?.Key?.Uid;

    if (!userId || !userUid) {
      throw new Error('Invalid session response: Missing required user data');
    }

    const mapper = new Octopus.SessionMapper();
    const session = mapper.from(result.Data);

    return forkJoin({
      session: of(session),
      user: this.users.getByKey(userId),
      settings: this.getSetting(userUid),
      configuration: this.configuration.getSystemConfiguration(),
    });
  }

  private enrichSessionData(result: AssembledSessionData): Session {
    if (!result?.session) {
      throw new Error('Session data not found in enrichment result');
    }

    if (!result.configuration) {
      throw new Error('System configuration not found in enrichment result');
    }

    result.session.systemConfiguration = result.configuration;

    if (!result.user) {
      throw new Error('User data not found in session result');
    }

    result.session.user = result.user;
    result.session.showDisabledUsers =
      result.settings?.find(
        (setting) => setting.code3 === ConfigurationSettingId.showDisabledUsers,
      )?.textValue === 'yes';
    result.session.culture =
      result.settings?.find(
        (setting) => setting.code4 === ConfigurationSettingId.culture,
      )?.textValue ?? this.locale.defaultLocale;

    return result.session;
  }

  private initializeLocaleAndFeatures(session: Session): Observable<Session> {
    return forkJoin({
      session: of(session),
      feature: this.context.isCloud
        ? this.feature.initialize(session)
        : of(null),
      locale: this.locale.set(this.outlookStore.language() ?? session.culture),
    }).pipe(
      tap((result) => {
        const timeFormats = result.locale.calendar?.timeFormats;
        this.outlookStore.setTimeFormats(
          timeFormats?.short ?? 'h:mm a',
          timeFormats?.long ?? 'h:mm:ss a z',
        );
      }),
      map((result) => result.session),
    );
  }

  private finalizeSessionSetup(session: Session | undefined): void {
    if (session) {
      this.lastSessionErrorType = undefined;
      this.global.setSession(session);
    }
  }

  private storeSessionResponseInStorage(
    session: Octopus.SessionResponse,
  ): void {
    try {
      const cachedData: CachedSessionData = {
        session,
        timestamp: Date.now(),
      };
      const responseString = JSON.stringify(cachedData);

      const cacheKey = this.sessionCacheKey;
      localStorage.setItem(cacheKey, responseString);
    } catch (error) {
      this.logError('session-cache-store-failed', error);
    }
  }

  private retrieveSessionResponseFromStorage(
    storageKey: string,
  ): Octopus.SessionResponse | null {
    try {
      const sessionString = localStorage.getItem(storageKey);

      if (sessionString) {
        const cachedData = JSON.parse(sessionString) as CachedSessionData;
        const cacheAge = Date.now() - cachedData.timestamp;

        if (cacheAge < this.SESSION_CACHE_DURATION_MS) {
          return cachedData.session;
        } else {
          localStorage.removeItem(storageKey);
        }
      }

      return null;
    } catch (error) {
      this.logError('session-cache-read-failed', error);
      return null;
    }
  }

  private handleSessionError(error: unknown): Observable<Session | undefined> {
    const eventName = this.categorizeSessionError(error);
    this.lastSessionErrorType = eventName;
    const errorMessage = `Session request failed after retries: ${normalizeError(error)}`;

    if (eventName === 'session-network-error') {
      const existingSession = this.global.session();
      if (existingSession) {
        this.logSessionEvent(
          'session-network-error-ignored',
          errorMessage,
          this.context.token,
          { reusedInMemorySession: true },
        );
        return of(existingSession);
      }
    }

    if (eventName === 'session-authentication-failed') {
      this.logError(
        eventName,
        new Error(`${errorMessage} - Authentication token invalid or expired`),
      );
    } else {
      this.logError(eventName, new Error(errorMessage));
    }

    return of(undefined);
  }

  private categorizeSessionError(error: unknown): string {
    const message = (error instanceof Error ? error.message : '').toLowerCase();
    const name = error instanceof Error ? error.name : '';

    if (error && typeof error === 'object' && 'status' in error) {
      const httpError = error as any;
      if (httpError.status === 0) {
        return 'session-network-error';
      }
      if (httpError.status === 401) {
        return 'session-authentication-failed';
      }
    }

    if (
      message.includes('authentication failed') ||
      message.includes('invalid token') ||
      message.includes('unauthorized') ||
      message.includes('authenticationexception')
    ) {
      return 'session-authentication-failed';
    }

    if (message.includes('session validation failed')) {
      return 'session-validation-error';
    }
    if (
      message.includes('missing required user data') ||
      message.includes('missing user key object')
    ) {
      return 'session-response-error';
    }
    if (message.includes('user key values')) {
      return 'session-userkey-error';
    }
    if (message.includes('missing addressbook data')) {
      return 'session-addressbook-error';
    }
    if (message.includes('missing data object')) {
      return 'session-malformed-response';
    }
    if (name === 'TimeoutError') {
      return 'session-timeout-error';
    }
    return 'session-pipeline-error';
  }

  private getSetting(user: string): Observable<ConfigurationSetting[] | null> {
    return this.configuration.getSettings([
      {
        code1: 'AMGR',
        code2: user,
        code3: ConfigurationSettingId.showDisabledUsers,
      },
      {
        code2: user,
        code3: 'Preferences',
        code4: ConfigurationSettingId.culture,
      },
    ]);
  }

  clearSessionCache(): void {
    this.sessionRequest = undefined;

    try {
      const cacheKey = this.sessionCacheKey;
      const cacheExists = localStorage.getItem(cacheKey) !== null;

      if (cacheExists) {
        localStorage.removeItem(cacheKey);
      }
    } catch (error) {
      this.logError('session-cache-clear-failed', error);
    }
  }

  logout(trigger: string): void {
    this.oauth2Service.clearAuth('manual-logout', { trigger });
    this.clearSessionCache();
    this.context.clearAuthentication();
    sessionStorage.clear();
    localStorage.clear();

    const destinationUrl = globalThis.location.origin + LOGIN_PATH;
    if (destinationUrl.startsWith(globalThis.location.origin)) {
      globalThis.location.href = destinationUrl;
    }
  }

  getCurrentUser(): Observable<User> {
    return this.getInfo().pipe(
      switchMap((session) => {
        if (!session?.user) {
          const error = new Error(
            'Current user is not available from the session.',
          );
          this.logError('session-user-unavailable', error);
          throw error;
        }
        return of(session.user);
      }),
    );
  }

  getUsersByPreference(): Observable<User[]> {
    return forkJoin({
      session: this.getInfo(),
      users: this.users.getUsers(),
    }).pipe(
      map(({ users, session }) => {
        if (!session) return [];
        return users.filter(
          (user) => session.showDisabledUsers ?? user.webAccess,
        );
      }),
    );
  }

  private logError(
    eventName: string,
    error: unknown,
    additionalMetadata?: Record<string, unknown>,
  ): void {
    const errorMessage = normalizeError(error);
    const errorObj = error instanceof Error ? error : new Error(errorMessage);

    this.logSessionEvent(
      eventName,
      errorMessage,
      this.context.token,
      additionalMetadata,
    );
    this.insightsService.trackException({
      error: errorObj,
      properties: { component: 'OutlookSessionService' },
    });
  }

  private logSessionEvent(
    name: string,
    error?: string,
    token?: string,
    metadata?: Record<string, unknown>,
  ): void {
    this.insightsService.trackEvent({ name }, SeverityLevel.Information, {
      component: 'OutlookSessionService',
      eventId: OutlookInsightsEvents.OutlookAuth,
      decodedToken: getDecodedAccessToken(token),
      ...(error && { error }),
      ...metadata,
    });
  }
}
